import React from "react";
import { Card, CircularProgress, Tag } from "@chakra-ui/react";
import "../../Animations.css";
import ContinueButton from "../Buttons/ContinueButton";
import { CourseLevel } from "@shared_types";
import { HiMiniCheckBadge } from "react-icons/hi2";
import { getProgressColors, getLevelColor } from "../../styles/courseStyles";
import {
  getCardBaseStyles,
  getCardLayoutStyles,
  getCardContentStyles,
  getTagStyles,
} from "../../styles/cardStyles";
import { useCardPressAnimationAndHaptic } from "../../hooks/useCardPressAnimation";

interface SquareCardProps {
  emoji: string | React.ReactNode;
  title: string;
  selected?: boolean;
  subtitle?: string;
  onClick?: () => void;
  completion?: number;
  level?: CourseLevel;
  progress?: number;
  chapters?: string;
}

const CoursesCardSquare: React.FC<SquareCardProps> = ({
  emoji,
  title,
  selected = false,
  subtitle = "",
  onClick,
  completion,
  level,
  progress,
}) => {
  const { isPressed, handleCardClick } =
    useCardPressAnimationAndHaptic(onClick);
  const { progressColor, progressTrackColor } = getProgressColors(progress);
  const levelColor = getLevelColor(level);
  const baseStyles = getCardBaseStyles(selected, isPressed);
  const layoutStyles = getCardLayoutStyles("square");
  const contentStyles = getCardContentStyles(
    completion ? "default" : "centered"
  );

  const handleCardClickWrapper = (e: React.MouseEvent) => {
    // Ignore clicks that originate from the button
    if (!(e.target as HTMLElement).closest("button")) {
      handleCardClick();
    }
  };

  return (
    <Card
      direction={"column"}
      justifyContent={"space-between"}
      alignItems={"start"}
      {...layoutStyles}
      {...baseStyles}
      className={`${baseStyles.className} ${contentStyles.className}`}
      position="relative"
      onClick={handleCardClickWrapper}
      style={{ flex: 1 }}
    >
      <div className="flex absolute top-[22px] right-[22px] items-end">
        {(() => {
          if (progress !== undefined && progress === 100) {
            return (
              <HiMiniCheckBadge
                className="text-[var(--color-green)]"
                size={42}
              />
            );
          } else if (progress !== undefined && progress !== 0) {
            return (
              <CircularProgress
                value={progress}
                color={progressColor}
                trackColor={progressTrackColor}
                size={"40px"}
                thickness={"16px"}
              />
            );
          } else {
            return null;
          }
        })()}
      </div>
      <div
        className="pl-1"
        style={{ fontSize: "var(--base-font-size-6xlarge)" }}
      >
        {emoji}
      </div>
      <div className="flex flex-col w-full gap-1">
        <Tag {...getTagStyles("level")} color={levelColor}>
          {level?.name?.toUpperCase()}
        </Tag>
        <div className="flex flex-row justify-start items-center gap-4">
          <div className="font-bold text-2xl text-left">{title}</div>
        </div>

        <div className="text-[var(--color-grey)] text-sm flex-grow text-left mb-2">
          {subtitle}
        </div>
        <ContinueButton
          className="w-full"
          text="Continue"
          onClick={() => onClick?.()}
        />
      </div>
    </Card>
  );
};

export default CoursesCardSquare;
